// ============================================================================
// LateRegBadge - compact countdown chip for tournament late-reg / rebuy window
// ============================================================================
// Renders inline in the tournament header. Shows live time remaining until
// lateRegistrationEndTime; once closed, the chip disappears. Anyone seated
// (or spectating) can glance at it to know whether new players can still
// register and whether busted players can still rebuy.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { Clock } from 'lucide-react';
import { POKER_COLORS } from '../../theme/designSystem';

interface LateRegBadgeProps {
  lateRegEndAt?: Date | number | null;
  currentBlindLevel?: number;
  lateRegLevels?: number;
  paused?: boolean;   // tournament on break - the clock (and late reg) is stopped
}

const LateRegBadge: React.FC<LateRegBadgeProps> = ({
  lateRegEndAt,
  currentBlindLevel = 1,
  lateRegLevels = 4,
  paused = false,
}) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!lateRegEndAt) return null;

  const cutoffMs = typeof lateRegEndAt === 'number'
    ? lateRegEndAt
    : new Date(lateRegEndAt as any).getTime();
  const remainingMs = cutoffMs - now;
  if (remainingMs <= 0) return null;
  if (currentBlindLevel > lateRegLevels) return null;

  const total = Math.floor(remainingMs / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  const text =
    m >= 60
      ? `${Math.floor(m / 60)}h ${m % 60}m`
      : `${m}m ${s.toString().padStart(2, '0')}s`;

  const closing = remainingMs < 60_000;

  return (
    <Box
      bg={closing ? 'rgba(239, 68, 68, 0.18)' : `${POKER_COLORS.gold}22`}
      border="1px solid"
      borderColor={closing ? '#ef4444' : POKER_COLORS.gold}
      borderRadius="full"
      px={3}
      py={1}
      title="Players can register and busted players can rebuy until this timer runs out."
    >
      <HStack spacing={2}>
        <Clock size={12} color={closing ? '#ef4444' : POKER_COLORS.gold} />
        <Text fontSize="xs" color={closing ? '#fca5a5' : POKER_COLORS.gold} fontWeight="bold" letterSpacing="wide">
          LATE REG · {paused ? 'PAUSED (break)' : text}
        </Text>
      </HStack>
    </Box>
  );
};

export default LateRegBadge;
